// @flow

import type { AuthBloc } from "../../blocs/authBloc";
import { apiAxios } from "../../config/AxiosConfig";
import { AppToaster } from "../../components/Toaster/Toaster";
import { Intent, Spinner } from "@blueprintjs/core";

class NotesApi {
  authBloc: AuthBloc;

  constructor(authBloc: AuthBloc) {
    this.authBloc = authBloc;
  }

  async getAll(metaData: any): Promise<Array> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.get(
      `tenants/${tenantId}/projects/${metaData?.projectId}/notes?offset=${metaData?.offSet}&limit=${metaData?.rowsPerPage}&q=${metaData.q}`,
      this.buildHeaders()
    );
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async createNote(createNoteRequest): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(`/tenants/${tenantId}/notes`, createNoteRequest, {...this.buildHeaders(), skipErrorToast: true});

      if (response.status === 201) {
        AppToaster.show({
          message: "Note Created",
          intent: Intent.PRIMARY
        });
        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to create note. Please try again.',
        intent: Intent.DANGER
      });
    }
  };

  async addAttachments(createNoteRequest): Promise<any> {
    const tenantId = this.authBloc.getCurrentTenant();

    const payload = createNoteRequest?.payload;
    delete createNoteRequest?.payload;

    const response = await apiAxios.post(`/tenants/${tenantId}/notes/attachments`, createNoteRequest, this.buildHeaders());
    createNoteRequest.payload = payload;
    if (response?.status === 201) {
      return response?.data;
    } else {
      throw new Error(response?.status);
    }
  };

  async updateNote(updateNoteRequest, nodeId): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.put(`/tenants/${tenantId}/notes/${nodeId}`, updateNoteRequest, {...this.buildHeaders(), skipErrorToast: true});

      if (response.status === 201) {
        AppToaster.show({
          message: "Note Updated",
          intent: Intent.PRIMARY
        });
        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to update note. Please try again.',
        intent: Intent.DANGER
      });
    }
  };

  async exportNotes(exportNoteRequest, fileName: string): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(`/tenants/${tenantId}/notes/download`, exportNoteRequest, {...this.buildHeaders(), skipErrorToast: true});

      if (response.status === 200) {
        // Response is { files: [{ fileName, url }] } — the generated notes PDF first,
        // followed by any PDFs attached to the selected notes. Each url is a signed S3 GET URL.
        // The notes PDF uses the backend's file name when it sends one, else the frontend export name.
        const files = response.data?.files || [];
        for (let i = 0; i < files.length; i++) {
          // Small gap between downloads so the browser doesn't drop/block subsequent ones
          if (i > 0) await new Promise((resolve) => setTimeout(resolve, 500));
          await this.downloadFile(i === 0 ? { ...files[i], fileName: files[i].fileName || fileName } : files[i]);
        }
        AppToaster.show({
          message: "Notes Exported Successfully",
          intent: Intent.PRIMARY
        });
        return;
      } else {
        throw new Error(response?.status);
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        AppToaster.show({
          message: 'No notes found for the given date range',
          intent: Intent.DANGER
        });
      } else {
        AppToaster.show({
          message: error?.response?.data?.message || error?.response?.data?.error || error?.response?.statusText || 'Something went wrong',
          intent: Intent.PRIMARY
        });
        // new Error(error);
      }
    }
  };

  async deleteNote(nodeId): Promise<any> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.delete(`/tenants/${tenantId}/notes/${nodeId}`, {...this.buildHeaders(), skipErrorToast: true});

      if (response.status === 201) {
        AppToaster.show({
          message: "Note Deleted",
          intent: Intent.PRIMARY
        });
        return response.data;
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to delete note. Please try again.',
        intent: Intent.DANGER
      });
    }
  };
  buildHeaders = () => {
    const accessToken = this.authBloc.getCurrentToken();
    const auth = "Bearer " + accessToken;
    return { headers: { Authorization: auth } };
  };

  // Fetch as a blob so the `download` attribute is honored (it's ignored for cross-origin
  // S3 links). Falls back to opening the signed URL directly if the fetch fails.
  downloadFile = async ({ fileName, url }) => {
    try {
      const fileResponse = await fetch(url, { cache: "no-store" });
      if (!fileResponse.ok) throw new Error(fileResponse.status);
      const blobUrl = window.URL.createObjectURL(await fileResponse.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      window.open(url, '_blank', 'noopener');
    }
  };
}

export default NotesApi;
